import React, { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const TYPE_COLOR = {
    assignment_new: '#7C6BEF',
    assignment_updated: '#F59E0B',
    assignment_reassigned: '#3B82F6',
    assignment_removed: '#EF4444',
    threshold_alert: '#EF4444',
    inspection_scheduled: '#002FA7',
    inspector_assigned: '#3B82F6',
    inspection_updated: '#F59E0B',
    dictamen_approved: '#00875A',
    dictamen_rejected: '#EF4444',
};

function timeAgo(iso) {
    if (!iso) return '';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    return `hace ${d} d`;
}

export default function NotificationBell({ variant = 'admin' }) {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get('/notifications', { params: { limit: 20 } });
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch (_) { /* ignore */ }
    };

    useEffect(() => {
        load();
        const iv = setInterval(load, 30000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const markRead = async (id) => {
        await api.patch(`/notifications/${id}/read`);
        load();
    };
    const markAllRead = async () => {
        setBusy(true);
        try { await api.patch('/notifications/read-all'); await load(); }
        finally { setBusy(false); }
    };

    const isDark = variant === 'onDark';
    const btnColor = isDark ? '#FFFFFF' : 'inherit';

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                data-testid="notification-bell"
                className="w-10 h-10 inline-flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 relative"
                style={{ color: btnColor }}
            >
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                    <span
                        data-testid="notification-badge"
                        className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full flex items-center justify-center"
                        style={{ background: '#EF4444' }}
                    >
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notification-panel"
                    className="absolute right-0 top-full mt-2 w-[380px] max-h-[520px] shadow-2xl z-50 overflow-hidden flex flex-col text-gray-900 dark:text-gray-100"
                    style={{ background: 'var(--card-bg, #FFFFFF)', border: '1px solid var(--border, #E5E7EB)' }}
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                        <div>
                            <div className="font-display font-black text-sm">Notificaciones</div>
                            <div className="text-[11px] opacity-60">
                                {unread > 0 ? `${unread} sin leer` : 'Sin nuevas'}
                            </div>
                        </div>
                        {unread > 0 && (
                            <button
                                onClick={markAllRead}
                                disabled={busy}
                                data-testid="notif-mark-all-read"
                                className="text-[11px] font-bold inline-flex items-center gap-1 hover:underline disabled:opacity-50"
                                style={{ color: '#10B981' }}
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />}
                                Marcar todas
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="px-4 py-8 text-center text-xs opacity-60">
                                Sin notificaciones aún
                            </div>
                        ) : (
                            items.map((n) => {
                                const color = TYPE_COLOR[n.type] || '#7C6BEF';
                                return (
                                    <button
                                        key={n.id}
                                        onClick={() => !n.read && markRead(n.id)}
                                        data-testid={`notif-${n.id}`}
                                        className={`w-full text-left px-4 py-3 border-b hover:bg-black/5 dark:hover:bg-white/5 flex gap-3 ${n.read ? '' : 'bg-[rgba(124,107,239,0.06)]'}`}
                                        style={{ borderColor: 'var(--border, #E5E7EB)' }}
                                    >
                                        <div className="w-1 shrink-0 self-stretch" style={{ background: color }} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <div className="text-xs font-bold truncate flex-1">{n.title}</div>
                                                {!n.read && (
                                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                                                )}
                                            </div>
                                            <div className="text-[11px] opacity-70 truncate">{n.body}</div>
                                            <div className="text-[10px] opacity-50 mt-1">{timeAgo(n.created_at)}</div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
